import { RouteComponentProps } from 'react-router-dom';

import { Nullable } from '@common/typescript/objects/Nullable';

import {
	ClinicPet,
	ServiceType,
	DeliveryType,
	PetOnHold, PetEngraving,
} from '@app/objects/Pet';
import { PriceKind } from '@app/objects/Price';
import { PetFile } from '@app/objects/PetFile';
import { PetPrice } from '@app/objects/PetPrice';
import { State } from '@app/objects/State';

export interface FormValues extends Omit<ClinicPet, 'genderId' | 'deliveryType' | 'serviceType' | 'crematory' | 'crematoryId'> {
	initialized?: boolean;
	genderId?: number;
	loadingUrns: boolean;
	deliveryType: DeliveryType | undefined;
	serviceType: ServiceType | undefined;
	products: Array<PetPrice>;
	urns: Array<PetPrice>;
}

export type Props = RouteComponentProps<{ id: string, type: string }>;
export type RequestMessageType = (CreateMessage | Partial<UpdateMessage>) | IncludeOptions;
export type Delivery = Pick<
	CreateMessage,
	'deliveryType'
	| 'deliveryAddress'
	| 'deliveryAddress2'
	| 'deliveryZip'
	| 'deliveryCity'
	| 'deliveryStateId'
	| 'deliveryClinicId'
	| 'deliveryLocationId'
	| 'deliveryTypeId'
>;

export interface PetPriceMessage extends Omit<PetPrice, 'clientId' | 'pet' | 'price' | 'editor' | 'editorId'> {
	priceKind?: PriceKind.ProductPrice | PriceKind.SpecialServicePrice;
}

interface IncludeOptions {
	withStatus: boolean;
	withCrematory: boolean;
	withSpecie: boolean;
	withBreed: boolean;
	withGender: boolean;
	withDiscount: boolean;
	withServices: boolean;
}

export interface CreateMessage {
	name: string;
	engraving: Array<PetEngraving>;

	ownerFirstName: string;
	ownerLastName: string;
	ownerPhone: string;
	ownerEmail: string;
	ownerStateId: Nullable<number>;
	ownerState: Nullable<State>;
	ownerAddress: string;
	ownerAddress2: string;
	ownerZip: string;
	ownerCity: string;

	petSpecieId: number;
	petBreedId: number;
	isMixed: boolean;

	serviceType?: ServiceType;

	color: string;
	reportedWeight: number;
	dateOfBirth: Nullable<number>;

	genderId: number;
	clinicLocationId: Nullable<number>;
	discountId: number | null;

	noAshes: boolean;
	specialInstructions: string;
	services: Array<PetPriceMessage>;

	rush: boolean;
	onHold: PetOnHold;

	deliveryType?: DeliveryType;
	deliveryTypeId?: Nullable<number>;
	deliveryAddress?: string;
	deliveryAddress2?: string;
	deliveryCity?: string;
	deliveryZip?: string;
	deliveryStateId?: Nullable<number>;
	deliveryClinicId?: Nullable<number>;
	deliveryLocationId?: Nullable<number>;

	internalIdNum: string;
	files: Array<PetFile>;
}

export interface UpdateMessage {
	id: number;
	name: string;
	engraving: Array<PetEngraving>;

	ownerFirstName: string;
	ownerLastName: string;
	ownerPhone: string;
	ownerEmail: string;
	ownerStateId: Nullable<number>;
	ownerState: Nullable<State>;
	ownerAddress: string;
	ownerAddress2: string;
	ownerZip: string;
	ownerCity: string;

	genderId: number;
	petSpecieId: number;
	petBreedId: number;
	isMixed: boolean;

	color: string;
	reportedWeight: number;
	dateOfBirth: Nullable<number>;
	serviceType?: ServiceType;
	noAshes: boolean;

	clinicLocationId: Nullable<number>;
	discountId: Nullable<number>;

	rush: boolean;
	onHold: PetOnHold;

	specialInstructions: string;
	services: Array<PetPriceMessage>;

	deliveryType?: DeliveryType;
	deliveryTypeId?: Nullable<number>;
	deliveryAddress?: string;
	deliveryAddress2?: string;
	deliveryCity?: string;
	deliveryZip?: string;
	deliveryStateId?: Nullable<number>;
	deliveryClinicId?: Nullable<number>;
	deliveryLocationId?: Nullable<number>;

	withCrematory?: boolean;
	withSpecie?: boolean;
	withBreed?: boolean;
	withGender?: boolean;
	withDiscount?: boolean;
	withServices?: boolean;
	withUrns?: boolean;

	internalIdNum: string;
	files: Array<PetFile>;
}

function clearPresetItems(list: Array<PetPrice> | undefined): Array<PetPrice> {
	if (!list) return [];

	return list.filter((item: PetPrice) => !item.parentId);
}

export function clearPreset(values: FormValues): FormValues {
	return {
		...values,

		services: clearPresetItems(values.services),
		urns: clearPresetItems(values.urns),
		products: clearPresetItems(values.products),
	};
}
